import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-icons + React 19 typings
import {
  FaArrowLeft as FaArrowLeftRaw,
  FaBook as FaBookRaw,
  FaClipboardList as FaClipboardListRaw,
  FaFilePdf as FaFilePdfRaw,
  FaTrash as FaTrashRaw,
  FaUpload as FaUploadRaw,
  FaUserFriends as FaUserFriendsRaw,
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../services/supabase';
import { teacherService } from '../../services/classroom/teacherService';
import ClassSectionSidebar from '../../components/classroom/ClassSectionSidebar';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaArrowLeft: any = FaArrowLeftRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaBook: any = FaBookRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaClipboardList: any = FaClipboardListRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaFilePdf: any = FaFilePdfRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaTrash: any = FaTrashRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaUpload: any = FaUploadRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaUserFriends: any = FaUserFriendsRaw;

const MOBILE_BREAKPOINT = '1024px';
const MAX_SYLLABUS_SIZE_BYTES = 50 * 1024 * 1024;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
`;

const BackRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.86rem;
  font-weight: 700;
  color: var(--textSecondary, #64748b);
  text-decoration: none;
  &:hover {
    color: var(--text, #0f172a);
  }
`;

const Hero = styled.section<{ $imageUrl?: string | null }>`
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  min-height: 210px;
  padding: 1.6rem;
  color: #fff;
  background:
    linear-gradient(120deg, rgba(11, 18, 32, 0.88), rgba(35, 64, 126, 0.8)),
    ${({ $imageUrl }) =>
      $imageUrl
        ? `url("${$imageUrl}") center/cover no-repeat`
        : 'radial-gradient(circle at 14% 18%, #334155 0%, #1e293b 48%, #0f172a 100%)'};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 1rem;
`;

const Breadcrumb = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  width: fit-content;
  font-size: 0.74rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 800;
  color: rgba(226, 232, 240, 0.92);
  background: rgba(15, 23, 42, 0.35);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 999px;
  padding: 0.28rem 0.7rem;
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-size: clamp(1.32rem, 2.4vw, 1.85rem);
  font-weight: 800;
  letter-spacing: -0.01em;
`;

const HeroStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
`;

const HeroPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.32rem 0.62rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #e2e8f0;
  background: rgba(15, 23, 42, 0.38);
  border: 1px solid rgba(148, 163, 184, 0.24);
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 236px minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const MainCol = styled.main`
  display: grid;
  gap: 0.95rem;
`;

const Card = styled.section`
  border: 1px solid color-mix(in srgb, var(--outline) 80%, transparent);
  border-radius: 14px;
  background: color-mix(in srgb, var(--surface) 94%, transparent);
  padding: 1rem;
`;

const CardTitle = styled.h2`
  margin: 0 0 0.85rem;
  font-size: 1rem;
  font-weight: 800;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.7rem;
  align-items: end;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  display: grid;
  gap: 0.35rem;
`;

const LabelText = styled.span`
  font-size: 0.8rem;
  color: var(--textSecondary, #64748b);
  font-weight: 700;
`;

const Input = styled.input`
  border: 1px solid color-mix(in srgb, var(--outline) 80%, transparent);
  border-radius: 10px;
  padding: 0.62rem 0.72rem;
  font: inherit;
  background: color-mix(in srgb, var(--surface) 96%, transparent);
  color: var(--text);
`;

const Hint = styled.p`
  margin: 0.4rem 0 0;
  font-size: 0.78rem;
  color: var(--textSecondary, #64748b);
`;

const UploadBtn = styled.button`
  border: 1px solid color-mix(in srgb, #1d4ed8 48%, transparent);
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: #fff;
  border-radius: 10px;
  padding: 0.64rem 0.92rem;
  font-size: 0.84rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const HiddenFileInput = styled.input`
  position: absolute;
  pointer-events: none;
  opacity: 0;
  width: 0;
  height: 0;
`;

const FilePickerBtn = styled.button`
  border: 1px solid color-mix(in srgb, var(--outline) 70%, transparent);
  background: color-mix(in srgb, var(--surfaceVariant) 36%, transparent);
  color: var(--text);
  border-radius: 10px;
  padding: 0.6rem 0.78rem;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.42rem;
`;

const StatusText = styled.p<{ $error?: boolean }>`
  margin: 0;
  font-size: 0.82rem;
  font-weight: 700;
  color: ${({ $error }) => ($error ? 'var(--error, #dc2626)' : 'var(--success, #16a34a)')};
`;

const Empty = styled.p`
  margin: 0;
  color: var(--textSecondary, #64748b);
  font-size: 0.84rem;
`;

const Row = styled.article`
  border: 1px solid color-mix(in srgb, var(--outline) 75%, transparent);
  border-radius: 12px;
  padding: 0.75rem 0.8rem;
  display: flex;
  gap: 0.65rem;
  align-items: center;
  justify-content: space-between;
`;

const RowMain = styled.div`
  min-width: 0;
  display: grid;
  gap: 0.16rem;
`;

const RowTitle = styled.p`
  margin: 0;
  font-size: 0.88rem;
  font-weight: 800;
  color: var(--text);
  overflow-wrap: anywhere;
`;

const RowMeta = styled.p`
  margin: 0;
  font-size: 0.76rem;
  color: var(--textSecondary, #64748b);
`;

const RowActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.46rem;
`;

const ActionLink = styled.a`
  border-radius: 8px;
  border: 1px solid color-mix(in srgb, var(--outline) 74%, transparent);
  padding: 0.42rem 0.62rem;
  font-size: 0.74rem;
  font-weight: 700;
  color: var(--text);
  text-decoration: none;
`;

const ActionDanger = styled.button`
  border-radius: 8px;
  border: 1px solid color-mix(in srgb, #ef4444 45%, transparent);
  background: color-mix(in srgb, #ef4444 14%, transparent);
  color: #b91c1c;
  padding: 0.42rem 0.62rem;
  font-size: 0.74rem;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

function trimPdfExtension(value: string): string {
  return value.replace(/\.pdf$/i, '').trim();
}

const ClassroomTeacherClassSyllabusPage: React.FC = () => {
  const { classId = '' } = useParams<{ classId: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [overviewQuery, lessonsQuery, assignmentsQuery, quizzesQuery] = useQueries({
    queries: [
      { queryKey: ['classroom', 'teacher', 'overview'], queryFn: teacherService.getOverview },
      { queryKey: ['classroom', 'teacher', 'lessons'], queryFn: teacherService.getLessons },
      { queryKey: ['classroom', 'teacher', 'work', 'assignment'], queryFn: () => teacherService.getWork('assignment') },
      { queryKey: ['classroom', 'teacher', 'work', 'quiz'], queryFn: () => teacherService.getWork('quiz') },
    ],
  });

  const syllabusQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'syllabus', classId],
    queryFn: () => teacherService.getClassSyllabus(classId),
    enabled: Boolean(classId.trim()),
  });

  const classItem = useMemo(
    () => (overviewQuery.data ?? []).find((row) => row.id === classId),
    [overviewQuery.data, classId]
  );
  const classLessonsCount = useMemo(
    () => (lessonsQuery.data ?? []).filter((row) => row.class_id === classId).length,
    [lessonsQuery.data, classId]
  );
  const classAssignmentsCount = useMemo(
    () =>
      (assignmentsQuery.data ?? []).filter((row) => row.class_id === classId).length +
      (quizzesQuery.data ?? []).filter((row) => row.class_id === classId).length,
    [assignmentsQuery.data, quizzesQuery.data, classId]
  );

  const uploadMutation = useMutation({
    mutationFn: async () => {
      const file = pendingFile;
      if (!file) throw new Error('Please select a PDF file first.');
      if (!classId.trim()) throw new Error('Class id is missing.');
      if (file.size > MAX_SYLLABUS_SIZE_BYTES) throw new Error('PDF must be 50MB or smaller.');
      const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
      if (!isPdf) throw new Error('Only PDF files are allowed.');

      const previous = await teacherService.getClassSyllabus(classId);
      const storagePath = teacherService.buildClassSyllabusStoragePath(classId, file.name);
      const { error: uploadError } = await supabase.storage
        .from(teacherService.textbookAssetBucket)
        .upload(storagePath, file, { upsert: false, contentType: 'application/pdf' });
      if (uploadError) throw new Error(uploadError.message);

      const { data } = supabase.storage.from(teacherService.textbookAssetBucket).getPublicUrl(storagePath);
      const storedTitle = title.trim() || trimPdfExtension(file.name) || 'Class syllabus';
      const saveResult = await teacherService.upsertClassSyllabus({
        classId,
        title: storedTitle,
        filePath: storagePath,
        fileUrl: data.publicUrl,
        uploadedBy: user?.id ?? null,
      });
      if (!saveResult.ok) {
        await supabase.storage.from(teacherService.textbookAssetBucket).remove([storagePath]);
        throw new Error(saveResult.error);
      }

      const previousPath = previous?.file_path?.trim() ?? '';
      if (previousPath && previousPath !== storagePath) {
        await supabase.storage.from(teacherService.textbookAssetBucket).remove([previousPath]);
      }

      setPendingFile(null);
      setTitle('');
      return `Syllabus "${storedTitle}" saved.`;
    },
    onSuccess: async (message) => {
      setSuccess(message);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'syllabus', classId] });
    },
    onError: (uploadError: unknown) => {
      setSuccess(null);
      setError((uploadError as { message?: string }).message ?? 'Unable to upload syllabus.');
    },
  });

  const removeSyllabus = async () => {
    if (deleting) return;
    setDeleting(true);
    setError(null);
    setSuccess(null);
    try {
      const existing = await teacherService.getClassSyllabus(classId);
      const removeResult = await teacherService.deleteClassSyllabus(classId);
      if (!removeResult.ok) {
        setError(removeResult.error);
        return;
      }
      const filePath = existing?.file_path?.trim() ?? '';
      if (filePath) {
        await supabase.storage.from(teacherService.textbookAssetBucket).remove([filePath]);
      }
      setSuccess('Deleted syllabus PDF.');
      await queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'syllabus', classId] });
    } finally {
      setDeleting(false);
    }
  };

  if (overviewQuery.isLoading || lessonsQuery.isLoading || assignmentsQuery.isLoading || quizzesQuery.isLoading || syllabusQuery.isLoading) {
    return <Page>Loading class syllabus workspace…</Page>;
  }

  if (!classItem || overviewQuery.isError || lessonsQuery.isError || assignmentsQuery.isError || quizzesQuery.isError || syllabusQuery.isError) {
    return <Page>Unable to load this class syllabus workspace right now.</Page>;
  }

  const syllabus = syllabusQuery.data;
  const shortClassName = classItem.name.length > 20 ? `${classItem.name.slice(0, 19)}…` : classItem.name;

  return (
    <Page>
      <BackRow>
        <BackLink to="/classroom-teacher/classes">
          <FaArrowLeft size={12} aria-hidden />
          Back to Classes
        </BackLink>
      </BackRow>

      <Hero $imageUrl={classItem.class_image_url}>
        <Breadcrumb>{classItem.name}</Breadcrumb>
        <HeroTitle>Class syllabus</HeroTitle>
        <HeroStats>
          <HeroPill>
            <FaUserFriends size={14} aria-hidden />
            {classItem.activeStudents} Students
          </HeroPill>
          <HeroPill>
            <FaBook size={14} aria-hidden />
            {classLessonsCount} Lessons
          </HeroPill>
          <HeroPill>
            <FaClipboardList size={14} aria-hidden />
            {classAssignmentsCount} Assignments
          </HeroPill>
          <HeroPill>
            <FaFilePdf size={14} aria-hidden />
            {syllabus ? 'Syllabus set' : 'No syllabus'}
          </HeroPill>
        </HeroStats>
      </Hero>

      <LayoutGrid>
        <ClassSectionSidebar classId={classId} className={shortClassName} />

        <MainCol>
          <Card>
            <CardTitle>{syllabus ? 'Replace syllabus PDF' : 'Upload syllabus PDF'}</CardTitle>
            <FormGrid>
              <Label>
                <LabelText>Syllabus title</LabelText>
                <Input
                  placeholder="Leave blank to use file name"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={140}
                />
              </Label>
              <UploadBtn type="button" onClick={() => uploadMutation.mutate()} disabled={uploadMutation.isPending || !pendingFile}>
                <FaUpload size={12} aria-hidden />
                {uploadMutation.isPending ? 'Saving...' : syllabus ? 'Replace syllabus' : 'Upload syllabus'}
              </UploadBtn>
            </FormGrid>
            <div style={{ marginTop: '0.62rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <HiddenFileInput
                id="class-syllabus-upload"
                type="file"
                accept="application/pdf,.pdf"
                onChange={(event) => {
                  const file = event.target.files?.[0] ?? null;
                  setPendingFile(file);
                  setError(null);
                  setSuccess(null);
                  if (file && !title.trim()) setTitle(trimPdfExtension(file.name));
                }}
              />
              <FilePickerBtn type="button" onClick={() => document.getElementById('class-syllabus-upload')?.click()}>
                <FaFilePdf size={11} aria-hidden />
                {pendingFile ? 'Replace PDF' : 'Choose PDF'}
              </FilePickerBtn>
              <Hint style={{ margin: 0 }}>
                {pendingFile ? `${pendingFile.name}` : 'No file selected yet.'}
              </Hint>
            </div>
            <Hint>Only one syllabus PDF is kept per class. Uploading a new file replaces the previous one.</Hint>
            {error ? <StatusText $error>{error}</StatusText> : null}
            {success ? <StatusText>{success}</StatusText> : null}
          </Card>

          <Card>
            <CardTitle>Current syllabus</CardTitle>
            {!syllabus ? (
              <Empty>No syllabus PDF uploaded yet.</Empty>
            ) : (
              <Row>
                <RowMain>
                  <RowTitle>{syllabus.title}</RowTitle>
                  <RowMeta>Added {new Date(syllabus.created_at).toLocaleString()}</RowMeta>
                </RowMain>
                <RowActions>
                  <ActionLink href={syllabus.file_url} target="_blank" rel="noreferrer">
                    Open PDF
                  </ActionLink>
                  <ActionDanger type="button" onClick={() => void removeSyllabus()} disabled={deleting}>
                    <FaTrash size={10} aria-hidden />
                    {deleting ? 'Deleting...' : 'Delete'}
                  </ActionDanger>
                </RowActions>
              </Row>
            )}
          </Card>
        </MainCol>
      </LayoutGrid>
    </Page>
  );
};

export default ClassroomTeacherClassSyllabusPage;
